import { Activity, Box, Braces, ChevronRight, Code2, GitBranch, Moon, Network, Play, ShieldCheck, Sun } from 'lucide-react';
import { MobileSiteMenu } from './components/MobileSiteMenu';
import { counterSource } from './sample';

export function ProductMark({ theme }: { theme?: 'dark' | 'light' }) {
  const logoSrc = theme === 'light' ? '/dark_logo.jpeg' : '/light_logo.jpeg';

  return (
    <div className="brand" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <img
        src={logoSrc}
        alt="Ortl Logo"
        style={{
          height: '34px',
          width: 'auto',
          objectFit: 'contain',
          display: 'block'
        }}
      />
      <em style={{ fontSize: '0.8rem', opacity: 0.75 }}>v1.0</em>
    </div>
  );
}

export function ThemeToggle({ theme, toggle }: { theme: 'dark' | 'light'; toggle: () => void }) {
  const next = theme === 'dark' ? 'light' : 'dark';
  return <button className="theme-toggle" onClick={toggle} aria-label={`Switch to ${next} mode`} title={`Switch to ${next} mode`}>{theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}<span>{next} mode</span></button>;
}

export function Landing({ openWorkspace, openAbout, theme, toggleTheme }: { openWorkspace: () => void; openAbout: () => void; theme: 'dark' | 'light'; toggleTheme: () => void }) {
  return <div className={`landing industrial-home theme-${theme}`}>
    <nav>
      <ProductMark theme={theme} />
      <div className="nav-links">
        <a href="#platform">Platform</a>
        <a href="#demo">Preview</a>
        <a href="#features">Capabilities</a>
        <a href="/about" onClick={(event) => { event.preventDefault(); openAbout(); }}>About</a>
        <ThemeToggle theme={theme} toggle={toggleTheme} />
        <button className="nav-cta" onClick={openWorkspace}>Launch workbench <ChevronRight size={14} /></button>
      </div>
      <MobileSiteMenu items={[{ label: 'Platform', href: '#platform' }, { label: 'Preview', href: '#demo' }, { label: 'Capabilities', href: '#features' }, { label: 'About', href: '/about', onSelect: openAbout }]} openWorkspace={openWorkspace} theme={theme} toggleTheme={toggleTheme} />
    </nav>
    <main>
      <section className="hero" id="platform">
        <div className="hero-copy">
          <div className="eyebrow"><span></span> RTL analysis workbench / v1.0</div>
          <h1>RTL becomes<br /><span>inspectable hardware.</span></h1>
          <p>Translate synthesizable Verilog and SystemVerilog into precise, interactive schematics. Trace buses, inspect sequential logic, and connect every component back to source.</p>
          <div className="hero-actions">
            <button className="primary" onClick={openWorkspace}><Play size={15} fill="currentColor" /> Open visualizer</button>
            <button className="secondary" onClick={openWorkspace}><Code2 size={16} /> Explore examples</button>
          </div>
          <div className="trust"><ShieldCheck size={14} /> Browser-based workflow · source is processed temporarily</div>
        </div>
        <aside className="hero-readout" aria-label="Analyzer capabilities">
          <header><Activity size={14} /><span>ANALYZER STATUS</span><b>READY</b></header>
          <dl>
            <div><dt>Input</dt><dd>Verilog / SystemVerilog</dd></div>
            <div><dt>Output</dt><dd>Hardware IR + schematic</dd></div>
            <div><dt>Routing</dt><dd>Orthogonal / semantic</dd></div>
            <div><dt>Inspection</dt><dd>Fan-in / fan-out / source</dd></div>
          </dl>
          <footer><i></i> LOCAL ANALYSIS SERVICE ONLINE</footer>
        </aside>
      </section>

      {/* CLEAN FEATURE VIDEO */}
      <section id="demo" style={{
        position: 'relative',
        margin: '4rem auto 5rem auto',
        maxWidth: '1200px',
        padding: '0 1.5rem',
        zIndex: 2
      }}>
        {/* Glow Effects */}
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '15%',
          width: '350px',
          height: '350px',
          background: 'radial-gradient(circle, rgba(255,107,0,0.22) 0%, transparent 70%)',
          filter: 'blur(50px)',
          pointerEvents: 'none',
          zIndex: 0
        }} />
        <div style={{
          position: 'absolute',
          bottom: '15%',
          right: '15%',
          width: '350px',
          height: '350px',
          background: 'radial-gradient(circle, rgba(0,210,255,0.18) 0%, transparent 70%)',
          filter: 'blur(55px)',
          pointerEvents: 'none',
          zIndex: 0
        }} />

        {/* Video Wrapper */}
        <div style={{
          position: 'relative',
          borderRadius: '16px',
          overflow: 'hidden',
          background: '#0a0d14',
          border: '1px solid rgba(255, 107, 0, 0.4)',
          boxShadow: '0 20px 60px -15px rgba(0,0,0,0.8), 0 0 35px -5px rgba(255,107,0,0.25)',
          zIndex: 1,
          aspectRatio: '16/9'
        }}>
          <video
            src="/feature-demo.mp4"
            autoPlay
            loop
            muted
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }}
          />
        </div>
      </section>

      <section className="system-strip" aria-label="Platform summary">
        <div><small>01 / PARSE</small><strong>Deterministic RTL analysis</strong></div>
        <div><small>02 / ELABORATE</small><strong>Hardware-aware topology</strong></div>
        <div><small>03 / INSPECT</small><strong>Interactive signal probing</strong></div>
      </section>

      <section className="product-shot" aria-label="Ortl EDA workspace preview">
        <div className="shot-bar">
          <span className="shot-brand">ORTL</span><b>counter.sv</b><div></div>
          <span className="ast-valid">● AST VALID</span><code>42 ms</code><code>4 nodes / 6 nets</code>
        </div>
        <div className="shot-body">
          <div className="code-preview">
            <div className="pane-title">RTL SOURCE <Code2 size={12} /></div>
            <pre>{counterSource.split('\n').map((line, index) => <div key={index}><span>{String(index + 1).padStart(2, ' ')}</span>{line}</div>)}</pre>
          </div>
          <div className="graph-preview">
            <div className="preview-grid"></div>
            <div className="preview-terminal in-a">&gt;] en</div>
            <div className="preview-terminal in-b">&gt;] clk</div>
            <div className="preview-alu"><small>ADD</small><strong>+</strong><em>[7:0]</em></div>
            <div className="preview-register"><small>REG</small><b>count</b><em>[7:0]</em><span>D&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Q</span><code>▷ CLK&nbsp; ○R&nbsp; E</code></div>
            <div className="preview-terminal out-a">count [7:0] ]&gt;</div>
            <i className="preview-wire bus-one"></i>
            <i className="preview-wire bus-two"></i>
            <i className="preview-wire clock-wire"></i>
          </div>
          <aside className="shot-inspector">
            <small>COMPONENT / REGISTER</small>
            <dl>
              <dt>Instance</dt><dd>count</dd>
              <dt>Width</dt><dd>[7:0]</dd>
              <dt>Clock</dt><dd>clk · posedge</dd>
              <dt>Reset</dt><dd>rst_n · async low</dd>
            </dl>
            <small>SOURCE MAPPING</small>
            <code>counter.sv:8–13</code>
            <small className="inspector-subtitle">FAN-OUT</small>
            <code>count → add_0 → count</code>
          </aside>
        </div>
      </section>

      <section className="feature-strip" id="features">
        <article>
          <span>01</span><Network />
          <h3>Trace complete dataflow</h3>
          <p>Select any component or net to illuminate its full fan-in and fan-out path while unrelated logic recedes.</p>
          <code>DATA / CLOCK / RESET</code>
        </article>
        <article>
          <span>02</span><Box />
          <h3>Inspect real hardware</h3>
          <p>Read widths, clocks, reset polarity, enable conditions, expressions, and direct RTL source ranges.</p>
          <code>REG / MUX / ALU / FSM</code>
        </article>
        <article>
          <span>03</span><GitBranch />
          <h3>Understand control logic</h3>
          <p>Correlate state transitions with the schematic using the embedded FSM viewer and source mapper.</p>
          <code>STATE / TRANSITION / SOURCE</code>
        </article>
        <article>
          <span>04</span><Braces />
          <h3>Learn from examples</h3>
          <p>Compare equivalent RTL styles and see how each coding pattern changes the inferred hardware structure.</p>
          <code>GUIDED EXAMPLE LAB</code>
        </article>
      </section>

      <section className="landing-cta">
        <div>
          <small>READY FOR ANALYSIS</small>
          <h2>Move from source code to signal path.</h2>
        </div>
        <button className="primary" onClick={openWorkspace}>Launch workbench <ChevronRight size={15} /></button>
      </section>
    </main>
  </div>;
}